import React, { useEffect, useState } from 'react';
import { TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import CloseIcon from '@material-ui/icons/Close';
import moment from 'moment-timezone';
import Joi from 'joi';
import { useSnackbar } from 'notistack';
import { API } from 'aws-amplify';
import { getAPIName } from '../../config';

type AuthorityForm = {
  name: string;
  agency_timezone: string;
  description: string;
  pgw_fee: string;
  payment_url: string;
};

export type AuthorityModalData = {
  id?: number;
  name?: string;
  agency_timezone?: string;
  description?: string | null;
  pgw_fee?: number | string | null;
  payment_url: string;
};

interface AddAuthorityModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  authority?: AuthorityModalData | null;
}

const validate_tollagency = Joi.object({
  name: Joi.string().required().messages({
    'any.required': 'Name is required',
    'string.empty': 'Name is required',
  }),
  description: Joi.string().allow(null, ''),
  agency_timezone: Joi.string().required().messages({
    'any.required': 'Timezone is required',
    'string.empty': 'Timezone is required',
  }),
  pgw_fee: Joi.number().allow(null),
  payment_url: Joi.string().allow(null,''),
});

const TIMEZONES = moment.tz.names().filter(tz => tz.startsWith('America/'));

const emptyForm = (): AuthorityForm => ({
  name: '',
  agency_timezone: '',
  description: '',
  pgw_fee: '',
  payment_url:''
});

export const AddAuthorityModal: React.FC<AddAuthorityModalProps> = ({
  open,
  onClose,
  onSuccess,
  authority,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState<AuthorityForm>(emptyForm());
  const isEditMode = authority?.id != null;

  useEffect(() => {
    if (!open) return;
    if (authority?.id) {
      setForm({
        name: authority.name || '',
        agency_timezone: authority.agency_timezone || '',
        description: authority.description || '',
        pgw_fee: authority.pgw_fee != null ? String(authority.pgw_fee) : '0.00',
        payment_url: authority.payment_url || ''
      });
    } else {
      setForm(emptyForm());
    }
  }, [open, authority]);

  const handleSubmit = async () => {
    try {
      const body = {
        ...form,
        description: form.description?.trim() || form.name,
        pgw_fee: form.pgw_fee !== '' ? Number(form.pgw_fee) : null,
      };
      const { error } = validate_tollagency.validate(body, { abortEarly: false });
      if (error) {
        enqueueSnackbar(error.details.map(d => d.message).join(', '), { variant: 'error' });
        return;
      }

      if (isEditMode) {
        await API.put(getAPIName(), `/tollagencies/${authority!.id}`, { body });
        enqueueSnackbar('Toll agency updated successfully', { variant: 'success' });
      } else {
        await API.post(getAPIName(), '/tollagencies', { body });
        enqueueSnackbar('Toll agency added successfully', { variant: 'success' });
      }

      onClose();
      setForm(emptyForm());
      onSuccess();
    } catch (error) {
      enqueueSnackbar((error as any)?.message || 'Failed', { variant: 'error' });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ style: { borderRadius: 10, padding: 8 } }}
    >
      <DialogTitle style={{ padding: '8px 40px 4px 16px' }}>
        {isEditMode ? 'Edit Authority' : 'Add Authority'}
        <IconButton
          size="small"
          onClick={onClose}
          style={{ position: 'absolute', top: 6, right: 6 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 25,
          padding: '8px 16px',
        }}
      >
        <TextField
          label="Name*"
          fullWidth
          variant="outlined"
          size="small"
          value={form.name}
          onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
          InputProps={{ style: { borderRadius: 6 } }}
        />

        <Autocomplete
          options={TIMEZONES}
          value={form.agency_timezone || null}
          onChange={(_, val) => setForm(p => ({ ...p, agency_timezone: val || '' }))}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Timezone*"
              fullWidth
              variant="outlined"
              size="small"
              InputProps={{ ...params.InputProps, style: { borderRadius: 6 } }}
            />
          )}
        />

        <TextField
          label="Description"
          fullWidth
          variant="outlined"
          size="small"
          value={form.description}
          onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
          InputProps={{ style: { borderRadius: 6 } }}
        />
        <TextField
          label="PGW Fee"
          fullWidth
          variant="outlined"
          size="small"
          type="number"
          inputProps={{ min: 0, step: '0.01' }}
          value={form.pgw_fee}
          onChange={(e) => setForm(p => ({ ...p, pgw_fee: e.target.value }))}
          InputProps={{ style: { borderRadius: 6 } }}
        />
          <TextField
            label="Payment Url"
            fullWidth
            variant="outlined"
            size="small"
            value={form.payment_url}
            onChange={(e) => setForm(p => ({ ...p, payment_url: e.target.value }))}
            InputProps={{ style: { borderRadius: 6 } }}
          />
      </DialogContent>

      <DialogActions
        style={{
          padding: '8px 16px 12px',
          justifyContent: 'center',
        }}
      >
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disableElevation
          style={{ borderRadius: 6, minWidth: 120 }}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};
